import React from 'react'
import { Button, makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components'
import { demandColumns } from '../../helpers/columnHelper'
import { DemandSearchHit } from '../../helpers/demandSearchHelper'
import { Loader } from '../../components/common/spinner/Loader'
import { UserMessage } from '../../components/common/messageBar/UserMessage'
import { DemandRowContent } from './DemandRowContent'
import { CustomQuestionRow } from '../Question/CustomQuestionRow'
import { SearchHitTextCell } from './SearchHitTextCell'
import { t } from '../../i18n/i18n'

/** These rows are neither paginated nor virtualised, so a broad query needs a ceiling. */
export const MAX_RENDERED_HITS = 200

const useStyles = makeStyles({
   row: {
      display: 'flex',
      gap: tokens.spacingVerticalS,
      boxSizing: 'border-box',
      borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
      padding: `${tokens.spacingVerticalS} 0`,
      '&:hover': {
         backgroundColor: tokens.colorNeutralBackground1Hover,
      },
      '&:active': {
         backgroundColor: tokens.colorNeutralBackground1Pressed,
      },
   },
   colheader: {
      fontWeight: tokens.fontWeightBold,
   },
   truncation: {
      marginBottom: tokens.spacingVerticalM,
   },
})

type DemandSearchResultListProps = {
   hits: DemandSearchHit[]
   normalizedQuery: string
   loading: boolean
   onClear: () => void
}

const noop = () => {}

/** Memoized: without it every keystroke re-rendered all 200 rows, which the hits don't require. */
const DemandSearchResultListInner = ({ hits, normalizedQuery, loading, onClear }: DemandSearchResultListProps): JSX.Element => {
   const classes = useStyles()
   const columns = demandColumns()
   const visibleHits = hits.slice(0, MAX_RENDERED_HITS)

   if (loading) return <Loader />

   if (hits.length === 0) {
      return (
         <UserMessage
            title="NoSearchResultsTitle"
            message="NoSearchResults"
            action={<Button onClick={onClear}>{t('ClearSearch')}</Button>}
         />
      )
   }

   return (
      <>
         {hits.length > MAX_RENDERED_HITS && (
            <MessageBar className={classes.truncation}>
               <MessageBarBody>{`${t('SearchResultsTruncated')} ${MAX_RENDERED_HITS} ${t('OfTotalHits')} ${hits.length}`}</MessageBarBody>
            </MessageBar>
         )}
         <div role="table">
            <div role="rowgroup">
               <div role="row" className={classes.row}>
                  {columns.map((col) => (
                     <span key={col.columnId} role="columnheader" className={classes.colheader} style={{ width: col.width, minWidth: col.minWidth }}>
                        {col.label}
                     </span>
                  ))}
               </div>
            </div>
            <div role="rowgroup">
               {visibleHits.map((hit) =>
                  hit.kind === 'demand' ? (
                     <DemandRowContent
                        key={`demand-${hit.demand.id}`}
                        item={hit.demand}
                        parent={hit.parent}
                        items={[]}
                        columns={columns}
                        rowClass={classes.row}
                        expanded={false}
                        expanding={false}
                        expandAllDemands={false}
                        showButton=""
                        setShowButton={noop}
                        toggleRowExpansion={noop}
                        flat
                        highlightQuery={normalizedQuery}
                        textContent={<SearchHitTextCell html={hit.demand.text} normalizedQuery={normalizedQuery} />}
                     />
                  ) : (
                     <CustomQuestionRow
                        key={`question-${hit.question.id}`}
                        question={hit.question}
                        demand={hit.demand}
                        columns={columns}
                        rowClass={classes.row}
                        flat
                        highlightQuery={normalizedQuery}
                        textContent={<SearchHitTextCell html={hit.question.text} normalizedQuery={normalizedQuery} />}
                     />
                  )
               )}
            </div>
         </div>
      </>
   )
}

export const DemandSearchResultList = React.memo(DemandSearchResultListInner)
